import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TabButton from '../atoms/TabButton';
import CodeInput from '../atoms/CodeInput';
import Label from '../atoms/Label';

/**
 * Code editor component with tabbed interface for HTML, CSS, and JavaScript
 */
const CodeEditor = ({ 
  htmlCode,
  cssCode,
  jsCode,
  onHtmlChange,
  onCssChange,
  onJsChange,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState('html');

  const tabs = [
    {
      id: 'html',
      label: 'HTML',
      value: htmlCode,
      onChange: onHtmlChange,
      placeholder: 'Enter your HTML markup...',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'css',
      label: 'CSS',
      value: cssCode,
      onChange: onCssChange,
      placeholder: 'Enter your CSS styles ...',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17v4a2 2 0 002 2h4" />
        </svg>
      )
    },
    {
      id: 'js',
      label: 'JavaScript',
      value: jsCode,
      onChange: onJsChange,
      placeholder: 'Enter your JavaScript code...',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  return (
    <div className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              icon={tab.icon}
              className="rounded-none border-b-0"
            >
              {tab.label}
            </TabButton>
          ))}
        </div>
      </div>

      {/* Code Input Area */}
      <div className="p-4">
        <Label htmlFor={`${activeTab}-input`} size="sm" className="mb-2">
          {activeTabData.label} Code
        </Label>
        <CodeInput
          id={`${activeTab}-input`}
          value={activeTabData.value || ''}
          onChange={activeTabData.onChange}
          placeholder={activeTabData.placeholder}
          language={activeTab === 'js' ? 'javascript' : activeTab}
          rows={12}
          className="min-h-[300px]"
        />
      </div>

      {/* Code Statistics */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
        <span>
          {activeTabData.value ? `${activeTabData.value.length} characters` : 'No code'}
        </span>
        <span>
          {activeTabData.value ? `${activeTabData.value.split('\n').length} lines` : '0 lines'}
        </span>
      </div>
    </div>
  );
};

CodeEditor.propTypes = {
  htmlCode: PropTypes.string,
  cssCode: PropTypes.string,
  jsCode: PropTypes.string,
  onHtmlChange: PropTypes.func.isRequired,
  onCssChange: PropTypes.func.isRequired,
  onJsChange: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default CodeEditor;
